import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { mockRentStatus, revenueData } from '../data/mockData';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiHome, FiPieChart, FiAlertTriangle, FiArrowRight } = FiIcons;

const StatCard = ({ title, value, subtext, icon, alert }) => (
  <div className="tech-panel p-6 flex flex-col justify-between bg-white relative overflow-hidden group">
    <div className="absolute top-0 right-0 p-2 opacity-5">
      <SafeIcon icon={icon} className="text-6xl text-[#004B87]" />
    </div>
    <div className="flex justify-between items-start mb-6 relative z-10">
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#004B87]/40">{title}</span>
      <div className={`p-2 border ${alert ? 'border-[#C1440E] bg-[#C1440E]/5' : 'border-[#004B87]/20'}`}>
        <SafeIcon icon={icon} className={`text-lg ${alert ? 'text-[#C1440E]' : 'text-[#004B87]/50'}`} />
      </div>
    </div>
    <div className="relative z-10">
      <h3 className={`text-4xl font-light tracking-tighter ${alert ? 'text-[#C1440E]' : 'text-[#004B87]'}`}>
        {value}
      </h3>
      <p className="text-[9px] uppercase mt-2 font-mono text-[#004B87]/40 flex items-center gap-2">
        <span className={alert ? 'text-[#C1440E]' : 'text-green-600'}>●</span> {subtext}
      </p>
    </div>
  </div>
);

export default function Dashboard() {
  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', axisPointer: { type: 'line', lineStyle: { color: '#004B87', type: 'dashed' } } },
    legend: { show: false },
    grid: { left: '2%', right: '2%', bottom: '5%', top: '5%', containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: revenueData.months, axisLine: { lineStyle: { color: 'rgba(0, 75, 135, 0.1)' } }, axisLabel: { fontFamily: 'monospace', fontSize: 9, color: '#004B87' } },
    yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: 'rgba(0, 75, 135, 0.05)', type: 'dashed' } }, axisLabel: { fontFamily: 'monospace', fontSize: 9, color: '#004B87' } },
    series: [
      { name: 'Collected', type: 'line', data: revenueData.collected, smooth: true, lineStyle: { color: '#004B87', width: 3 }, itemStyle: { color: '#004B87' }, areaStyle: { color: 'rgba(0, 75, 135, 0.05)' }, symbol: 'circle', symbolSize: 8 },
      { name: 'Projected', type: 'line', data: revenueData.projected, smooth: true, lineStyle: { color: '#C1440E', width: 2, type: 'dashed' }, itemStyle: { color: '#C1440E' }, symbol: 'none' }
    ]
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 md:p-8 space-y-8">
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="AUM Valuation" value="$12.4M" subtext="Trend: +2.4% Annual" icon={FiTrendingUp} />
        <StatCard title="Portfolio Reach" value="81" subtext="Units across 3 regions" icon={FiHome} />
        <StatCard title="Market Occupancy" value="92.5%" subtext="Target: 95.0% Q4" icon={FiPieChart} />
        <StatCard title="Critical Ops" value="4" subtext="Emergency maintenance" icon={FiAlertTriangle} alert />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 tech-panel p-8 bg-white">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h3 className="uppercase tracking-[0.2em] font-bold text-xs text-[#004B87]">Revenue Flow Analytics</h3>
              <p className="text-[9px] font-mono text-[#004B87]/40 mt-1 uppercase">Metric: Gross Collected vs Projected</p>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-[9px] font-mono uppercase">
                <div className="w-2 h-2 bg-[#004B87]"></div> Collected
              </div>
              <div className="flex items-center gap-2 text-[9px] font-mono uppercase">
                <div className="w-2 h-2 bg-[#C1440E]"></div> Projected
              </div>
            </div>
          </div>
          <ReactECharts option={chartOptions} style={{ height: '320px' }} />
        </div>

        <div className="tech-panel p-8 flex flex-col bg-white">
          <div className="flex justify-between items-center mb-8">
            <h3 className="uppercase tracking-[0.2em] font-bold text-xs text-[#004B87]">Unit Status</h3>
            <SafeIcon icon={FiArrowRight} className="text-[#004B87]/30" />
          </div>
          <div className="flex-1 overflow-auto">
            <div className="space-y-4">
              {mockRentStatus.map((rent, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-[#004B87]/10 hover:border-[#C1440E]/30 transition-colors group cursor-default">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#004B87]/5 border border-[#004B87]/20 flex items-center justify-center text-[10px] font-bold text-[#004B87] font-mono">
                      {rent.avatar}
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#004B87]">{rent.tenant}</p>
                      <p className="text-[9px] font-mono text-[#004B87]/40 uppercase">Unit {rent.unit} • {rent.amount}</p>
                    </div>
                  </div>
                  <div className={`text-[9px] font-mono uppercase px-2 py-1 border 
                    ${rent.status === 'paid' ? 'border-green-500/30 text-green-600 bg-green-50' : 
                      rent.status === 'overdue' ? 'border-[#C1440E] text-[#C1440E] bg-[#C1440E]/5' : 
                      'border-[#004B87]/30 text-[#004B87]/40'}`}>
                    {rent.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button className="w-full mt-8 btn-blueprint text-[10px]">Full Portfolio Ledger</button>
        </div>
      </div>

    </motion.div>
  );
}